import { useCallback, useEffect, useMemo, useState } from "react";

import {
  PRESENTATION_STORAGE_KEY,
  buildImprovementReport,
  buildImprovementReportJson,
  getPresentationBlocks,
} from "@/content/interactivePresentation038.js";

function loadStoredState() {
  try {
    const raw = localStorage.getItem(PRESENTATION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function defaultState() {
  return {
    trackId: "express",
    stepIndex: 0,
    completedIds: [],
    tourActive: false,
    notes: [],
  };
}

export function useInteractivePresentation038(locale) {
  const [state, setState] = useState(() => ({ ...defaultState(), ...loadStoredState() }));
  const [draftNote, setDraftNote] = useState({ type: "question", text: "" });
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [reportText, setReportText] = useState("");

  const blocks = useMemo(() => getPresentationBlocks(state.trackId, locale), [state.trackId, locale]);
  const activeBlock = blocks[state.stepIndex] ?? blocks[0];

  useEffect(() => {
    localStorage.setItem(PRESENTATION_STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (state.stepIndex >= blocks.length) {
      setState((s) => ({ ...s, stepIndex: Math.max(0, blocks.length - 1) }));
    }
  }, [blocks.length, state.stepIndex]);

  const setTrack = useCallback((trackId) => {
    setState((s) => ({ ...s, trackId, stepIndex: 0, completedIds: [] }));
  }, []);

  const goStep = useCallback(
    (index) => {
      setState((s) => ({
        ...s,
        stepIndex: Math.max(0, Math.min(index, blocks.length - 1)),
      }));
    },
    [blocks.length],
  );

  const markComplete = useCallback(() => {
    if (!activeBlock) return;
    setState((s) => ({
      ...s,
      completedIds: s.completedIds.includes(activeBlock.id)
        ? s.completedIds
        : [...s.completedIds, activeBlock.id],
    }));
  }, [activeBlock]);

  const resetDemo = useCallback(() => {
    setState((s) => ({ ...s, stepIndex: 0, completedIds: [], tourActive: false }));
  }, []);

  const toggleTour = useCallback((active) => {
    setState((s) => ({ ...s, tourActive: active }));
  }, []);

  const stepNotes = useMemo(
    () => state.notes.filter((n) => n.stepId === activeBlock?.id),
    [state.notes, activeBlock?.id],
  );

  const addNote = useCallback(() => {
    const text = draftNote.text.trim();
    if (!text || !activeBlock) return;
    const note = {
      id: `note-${Date.now()}`,
      stepId: activeBlock.id,
      trackId: state.trackId,
      zone: activeBlock.zone,
      type: draftNote.type,
      text,
      locale,
      createdAt: new Date().toISOString(),
    };
    setState((s) => ({ ...s, notes: [...s.notes, note] }));
    setDraftNote((d) => ({ ...d, text: "" }));
  }, [activeBlock, draftNote, locale, state.trackId]);

  const updateNote = useCallback((id, patch) => {
    setState((s) => ({
      ...s,
      notes: s.notes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    }));
    setEditingNoteId(null);
  }, []);

  const deleteNote = useCallback((id) => {
    setState((s) => ({ ...s, notes: s.notes.filter((n) => n.id !== id) }));
  }, []);

  const resetNotes = useCallback(() => {
    setState((s) => ({ ...s, notes: [] }));
  }, []);

  const generateReport = useCallback(() => {
    const md = buildImprovementReport({
      locale,
      trackId: state.trackId,
      blocks,
      notes: state.notes,
      completedIds: state.completedIds,
    });
    setReportText(md);
    return md;
  }, [blocks, locale, state.completedIds, state.notes, state.trackId]);

  const exportJson = useCallback(() => {
    return buildImprovementReportJson({
      locale,
      trackId: state.trackId,
      completedIds: state.completedIds,
      notes: state.notes,
      blocks: blocks.map((b) => ({ id: b.id, title: b.title })),
    });
  }, [blocks, locale, state.completedIds, state.notes, state.trackId]);

  return {
    state,
    blocks,
    activeBlock,
    stepNotes,
    draftNote,
    setDraftNote,
    editingNoteId,
    setEditingNoteId,
    reportText,
    setTrack,
    goStep,
    markComplete,
    resetDemo,
    toggleTour,
    addNote,
    updateNote,
    deleteNote,
    resetNotes,
    generateReport,
    exportJson,
  };
}
